import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { TextBox } from 'devextreme-react/text-box';
import { useScreenSize } from '../../../../../utils/media-query';
import { createPortal } from 'react-dom';

// Constantes para lazy loading
const INITIAL_LOAD_SIZE = 200; // Cargar inicialmente solo 200 elementos
const LOAD_MORE_SIZE = 100; // Cargar 100 más cada vez que se llegue al final

// Interfaz para data source con lazy loading
interface LazyDataSource {
  load: (options?: { skip?: number; take?: number; searchText?: string }) => Promise<SelectableItem[]>;
  getTotalCount?: (searchText?: string) => Promise<number>;
}
export interface SelectableItem {
  id: number | string;
  code?: string;
  name: string;
  group?: string;
  [key: string]: any;
}

// Props del componente genérico
interface GenericSearchSelectProps {
  dataSource: {
    load: (options?: { skip?: number; take?: number; searchText?: string }) => Promise<SelectableItem[]>;
    getTotalCount?: (searchText?: string) => Promise<number>;
  };
  onItemSelected: (item: SelectableItem) => void;
  selectedItems?: SelectableItem[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  searchFields?: string[];
  groupField?: string;
  displayFields?: { title: string; subtitle: string };
  showGroups?: boolean;
  minSearchLength?: number;
  keepLabel?: boolean; // Nueva prop para mantener el label después de seleccionar
}

export const GenericSearchSelect: React.FC<GenericSearchSelectProps> = ({
  dataSource,
  onItemSelected,
  selectedItems = [],
  placeholder = 'Buscar elementos...',
  label,
  disabled = false,
  className = '',
  searchFields = ['name', 'code'],
  groupField = 'group',
  displayFields = { title: 'name', subtitle: 'code' },
  showGroups = true,
  minSearchLength = 2,
  keepLabel = false, // Por defecto false para mantener comportamiento existente
}) => {
  const { isSmall, isXSmall } = useScreenSize();
  
  // Estados principales
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [items, setItems] = useState<SelectableItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  const inputRef = useRef<TextBox>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cargar datos con lazy loading
  const loadItems = useCallback(async (searchTerm?: string, isLoadMore = false) => {
    if (!dataSource?.load) return;
    
    if (isLoadMore) {
      if (loadingMore || !hasMoreData) return;
      setLoadingMore(true);
    } else {
      if (loading) return;
      setLoading(true);
    }
    
    try {
      const currentSearch = searchTerm !== undefined ? searchTerm : searchText.trim();
      const skip = isLoadMore ? items.length : 0;
      const take = isLoadMore ? LOAD_MORE_SIZE : INITIAL_LOAD_SIZE;
      
      const data = await dataSource.load({ 
        skip, 
        take, 
        searchText: currentSearch 
      });
      
      if (isLoadMore) {
        setItems(prev => [...prev, ...(data || [])]);
        setHasMoreData((data?.length || 0) >= LOAD_MORE_SIZE);
      } else {
        setItems(data || []);
        setHasMoreData((data?.length || 0) >= INITIAL_LOAD_SIZE);
        
        // Obtener count total si está disponible
        if (dataSource.getTotalCount) {
          const total = await dataSource.getTotalCount(currentSearch);
          setTotalCount(total);
        }
      }
    } catch (error) {
      console.error('Error cargando elementos:', error);
      if (!isLoadMore) {
        setItems([]);
        setTotalCount(0);
      }
      setHasMoreData(false);
    }
    
    setLoading(false);
    setLoadingMore(false);
  }, [dataSource, loading, loadingMore, hasMoreData, items.length, searchText]);

  // Cargar más elementos cuando se llegue al final
  const loadMoreItems = useCallback(() => {
    if (!loadingMore && hasMoreData && !loading) {
      loadItems(searchText, true);
    }
  }, [loadItems, loadingMore, hasMoreData, loading, searchText]);

  // Manejar clicks fuera del componente para cerrar dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const inputElement = inputRef.current?.instance?.element();
      const dropdownElement = dropdownRef.current;
      
      // Si el click no es en el input ni en el dropdown, cerrar
      if (showDropdown && 
          inputElement && !inputElement.contains(target) &&
          dropdownElement && !dropdownElement.contains(target)) {
        setShowDropdown(false);
      }
    };

    // Prevenir scroll del body en móviles cuando el modal está abierto
    if (showDropdown && (isXSmall || isSmall)) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.body.style.overflow = '';
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown, isXSmall, isSmall]);

  // Cargar datos iniciales cuando se abre el dropdown
  useEffect(() => {
    if (showDropdown && items.length === 0) {
      loadItems();
    }
  }, [showDropdown, items.length, loadItems]);

  // Sincronizar searchText con selectedItems cuando keepLabel es true
  useEffect(() => {
    if (keepLabel && selectedItems.length > 0) {
      const lastSelected = selectedItems[selectedItems.length - 1];
      const displayText = `${lastSelected[displayFields.title]}${lastSelected[displayFields.subtitle] ? ' - ' + lastSelected[displayFields.subtitle] : ''}`;
      setSearchText(displayText);
    } else if (keepLabel && selectedItems.length === 0) {
      setSearchText(''); // Limpiar si no hay selección
    }
  }, [keepLabel, selectedItems, displayFields]);

  // Recargar cuando cambia el texto de búsqueda
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const currentSearch = searchText.trim();
      
      // Solo recargar si:
      // 1. Cambió de vacío a texto válido, o
      // 2. Cambió de texto válido a vacío, o  
      // 3. Cambió el texto de búsqueda válido
      const shouldReload = 
        (currentSearch.length >= minSearchLength) || 
        (currentSearch.length === 0);

      if (shouldReload) {
        setItems([]); // Limpiar items existentes
        setHasMoreData(true); // Resetear flag
        setTotalCount(0); // Resetear count
        loadItems(currentSearch);
      }
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [searchText, minSearchLength, loadItems]);

  // Filtrar elementos solo para excluir ya seleccionados (el filtrado por texto se hace en el servidor)
  const filteredItems = useMemo(() => {
    const selectedIds = selectedItems.map(item => item.id);
    return items.filter(item => !selectedIds.includes(item.id));
  }, [items, selectedItems]);

  // Agrupar elementos
  const groupedItems = useMemo(() => {
    if (!showGroups) {
      return { 'Elementos': filteredItems };
    }

    const grouped: { [group: string]: SelectableItem[] } = {};
    
    filteredItems.forEach(item => {
      const group = item[groupField] || 'Sin Grupo';
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(item);
    });
    
    // Ordenar grupos y elementos
    const result: { [group: string]: SelectableItem[] } = {};
    Object.keys(grouped).sort().forEach(group => {
      result[group] = grouped[group].sort((a, b) => 
        a[displayFields.title]?.toString().localeCompare(b[displayFields.title]?.toString() || '') || 0
      );
    });
    
    return result;
  }, [filteredItems, showGroups, groupField, displayFields.title]);

  // Función para hacer scroll al elemento seleccionado
  const scrollToSelectedItem = useCallback((index: number) => {
    if (index < 0) return;
    
    // Buscar el elemento DOM correspondiente
    const elementId = `dropdown-item-${index}`;
    const element = document.getElementById(elementId);
    
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, []);

  // Manejar scroll para lazy loading
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    
    // Cargar más cuando esté cerca del final (90% del scroll)
    const scrollPercent = (scrollTop + clientHeight) / scrollHeight;
    
    if (scrollPercent > 0.9 && hasMoreData && !loadingMore && !loading) {
      loadMoreItems();
    }
  }, [hasMoreData, loadingMore, loading, loadMoreItems]);

  // Calcular posición del dropdown
  const getDropdownStyles = (): React.CSSProperties => {
    const inputElement = inputRef.current?.instance?.element();
    if (!inputElement) {
      return { display: 'none' };
    }

    const inputRect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // En móviles usar modal centrado
    if (isXSmall || isSmall || viewportWidth < 768) {
      return {
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        width: '100vw',
        height: '100vh',
        zIndex: 99999,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      };
    }

    // En desktop: calcular si hay espacio abajo o arriba
    const spaceBelow = viewportHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;
    const dropdownHeight = Math.min(350, Math.max(spaceBelow - 40, spaceAbove - 40));
    
    // Decidir si mostrar arriba o abajo
    const showAbove = spaceBelow < 200 && spaceAbove > spaceBelow;

    const styles = showAbove ? {
      position: 'fixed' as const,
      bottom: `${viewportHeight - inputRect.top + 2}px`,
      left: `${inputRect.left}px`,
      width: `${Math.max(inputRect.width, 700)}px`,
      height: `${dropdownHeight}px`,
      zIndex: 10000,
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '4px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    } : {
      position: 'fixed' as const,
      top: `${inputRect.bottom + 1}px`,
      left: `${inputRect.left}px`,
      width: `${Math.max(inputRect.width, 700)}px`,
      height: `${dropdownHeight}px`,
      zIndex: 10000,
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '4px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    };

    return styles;
  };

  // Manejar cambio de texto
  const handleSearchChange = useCallback((newValue: string) => {
    setSearchText(newValue);
    setSelectedIndex(-1);
    
    if (!showDropdown) {
      setShowDropdown(true);
    }
    
    if (items.length === 0 && !loading) {
      loadItems();
    }
  }, [showDropdown, items.length, loading, loadItems]);

  // Manejar selección de elemento
  const handleItemClick = useCallback((item: SelectableItem) => {
    onItemSelected(item);
    
    // Si keepLabel es true, mantener el nombre del elemento seleccionado
    if (keepLabel) {
      const displayText = `${item[displayFields.title]}${item[displayFields.subtitle] ? ' - ' + item[displayFields.subtitle] : ''}`;
      setSearchText(displayText);
    } else {
      setSearchText(''); // Comportamiento original: limpiar
    }
    
    setShowDropdown(false);
    setSelectedIndex(-1);
  }, [onItemSelected, keepLabel, displayFields]);

  // Eventos del TextBox
  const handleTextBoxEvents = {
    onInput: useCallback((e: any) => {
      const value = e.event?.target?.value || '';
      handleSearchChange(value);
    }, [handleSearchChange]),
    
    onValueChanged: useCallback((e: any) => {
      // Detectar cuando se limpia el campo con el botón X
      if (e.value === '' && searchText !== '') {
        // El usuario limpió el campo, notificar con null
        if (keepLabel) {
          onItemSelected(null as any); // Notificar que se limpió la selección
        }
        setSearchText('');
      }
    }, [searchText, keepLabel, onItemSelected]),
    
    onFocusIn: useCallback(() => {
      setShowDropdown(true); // Abrir inmediatamente al hacer focus
      if (items.length === 0 && !loading) {
        loadItems();
      }
    }, [items.length, loading, loadItems]),
    
    onFocusOut: useCallback((e: any) => {
      // Delay para permitir click en dropdown
      setTimeout(() => {
        if (!dropdownRef.current?.contains(document.activeElement)) {
          setShowDropdown(false);
        }
      }, 150);
    }, []),
  };

  // Manejar teclas
  const handleKeyDown = useCallback((e: any) => {
    const key = e.event?.key;
    if (!showDropdown || filteredItems.length === 0) return;

    const flatItems = Object.values(groupedItems).flat();
    
    switch (key) {
      case 'ArrowDown':
        e.event.preventDefault();
        const newIndexDown = Math.min(selectedIndex + 1, flatItems.length - 1);
        setSelectedIndex(newIndexDown);
        scrollToSelectedItem(newIndexDown);
        break;
      case 'ArrowUp':
        e.event.preventDefault();
        const newIndexUp = Math.max(selectedIndex - 1, -1);
        setSelectedIndex(newIndexUp);
        scrollToSelectedItem(newIndexUp);
        break;
      case 'Enter':
        e.event.preventDefault();
        if (selectedIndex >= 0 && flatItems[selectedIndex]) {
          handleItemClick(flatItems[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  }, [showDropdown, filteredItems, groupedItems, selectedIndex, handleItemClick, scrollToSelectedItem]);

  // Determinar contenido del dropdown
  const getDropdownContent = () => {
    if (loading) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          Cargando elementos...
        </div>
      );
    }

    if (searchText.length > 0 && searchText.length < minSearchLength) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          Ingrese al menos {minSearchLength} caracteres para buscar
        </div>
      );
    }

    if (filteredItems.length === 0 && searchText.length >= minSearchLength) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          No se encontraron elementos que coincidan con "{searchText}"
        </div>
      );
    }

    if (filteredItems.length === 0 && searchText.length === 0) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          {items.length === 0 ? 'No hay elementos disponibles' : 'Comience a escribir para buscar elementos'}
        </div>
      );
    }

    // Mostrar con agrupaciones en formato tabla
    return (
      <div 
        style={{ height: '100%', overflow: 'auto' }}
        onScroll={handleScroll}
      >
        {/* Header de tabla */}
        <div style={{
          display: 'flex',
          padding: '12px 16px',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          fontWeight: '600',
          fontSize: '12px',
          color: '#495057',
          textTransform: 'uppercase',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}>
          <div style={{ flex: '0 0 150px', paddingRight: '16px' }}>
            Código
          </div>
          <div style={{ flex: 1 }}>
            Nombre
          </div>
        </div>
        
        {Object.entries(groupedItems).map(([group, groupItems]) => (
          <div key={group}>
            {/* Header del grupo */}
            {showGroups && Object.keys(groupedItems).length > 1 && (
              <div style={{
                padding: '8px 16px',
                backgroundColor: '#e9ecef',
                fontWeight: '600',
                fontSize: '13px',
                color: '#495057',
                borderBottom: '1px solid #dee2e6',
                display: 'flex',
                alignItems: 'center',
              }}>
                <span>{group}</span>
                <span style={{ 
                  marginLeft: '8px', 
                  fontSize: '11px', 
                  color: '#6c757d',
                  fontWeight: '400' 
                }}>
                  ({groupItems.length})
                </span>
              </div>
            )}
            
            {/* Elementos del grupo */}
            {groupItems.map((item, index) => {
              const globalIndex = Object.values(groupedItems).flat().indexOf(item);
              return (
                <div
                  id={`dropdown-item-${globalIndex}`}
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: selectedIndex === globalIndex ? '#e3f2fd' : 'white',
                    minHeight: '36px',
                  }}
                  onMouseEnter={() => setSelectedIndex(globalIndex)}
                >
                  <div style={{ 
                    flex: '0 0 150px', 
                    paddingRight: '16px',
                    fontSize: '13px', 
                    color: '#333',
                    fontWeight: '400'
                  }}>
                    {item[displayFields.subtitle] || '-'}
                  </div>
                  <div style={{ 
                    flex: 1,
                    fontSize: '13px', 
                    color: '#333',
                    fontWeight: '400',
                    lineHeight: '1.2'
                  }}>
                    {item[displayFields.title]}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        
        {/* Indicador de carga */}
        {loadingMore && (
          <div style={{
            padding: '16px',
            textAlign: 'center',
            backgroundColor: 'rgba(0, 122, 204, 0.1)',
            fontSize: '12px',
            color: '#007acc',
          }}>
            Cargando más elementos...
          </div>
        )}
        
        {/* Footer con información */}
        <div style={{
          padding: '8px 16px',
          fontSize: '11px',
          color: '#999',
          borderTop: '1px solid #eee',
          backgroundColor: '#fafafa',
          textAlign: 'center',
        }}>
          {filteredItems.length} de {totalCount > 0 ? totalCount : '?'} elementos
          {!hasMoreData && filteredItems.length > INITIAL_LOAD_SIZE && (
            <div style={{ marginTop: '4px', color: '#666' }}>
              Todos los elementos cargados
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <TextBox
        ref={inputRef}
        value={searchText}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        label={label}
        labelMode="floating"
        showClearButton={true}
        {...handleTextBoxEvents}
        onKeyDown={handleKeyDown}
      />

      {/* Dropdown con resultados usando Portal */}
      {showDropdown && createPortal(
        <div 
          ref={dropdownRef} 
          style={getDropdownStyles()}
          onClick={(e) => {
            // En móviles, cerrar si se hace click en el overlay
            if ((isXSmall || isSmall) && e.target === e.currentTarget) {
              setShowDropdown(false);
            }
          }}
        >
          {/* Contenedor interno para móviles */}
          {(isXSmall || isSmall) ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '500px',
              height: '80vh',
              maxHeight: '600px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            }}>
              {/* Header móvil */}
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid #eee',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px 12px 0 0',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                }}>
                  <span style={{ fontWeight: '600', fontSize: '16px', color: '#333' }}>
                    {label || 'Buscar elementos'}
                  </span>
                  <button
                    onClick={() => setShowDropdown(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      padding: '4px',
                      color: '#666',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    ×
                  </button>
                </div>
                
                {/* Campo de búsqueda para móviles */}
                <TextBox
                  value={searchText}
                  placeholder={placeholder}
                  onInput={(e: any) => {
                    const value = e.event?.target?.value || '';
                    handleSearchChange(value);
                  }}
                  showClearButton={true}
                  style={{ width: '100%' }}
                />
              </div>

              {/* Contenido principal móvil */}
              <div style={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}>
                {getDropdownContent()}
              </div>
            </div>
          ) : (
            <>
              {/* Contenido principal desktop */}
              <div style={{
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}>
                {getDropdownContent()}
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};
